import axios from "axios";

import { API_URL } from "@/config/constants";
import { buildUrl } from "@/utils";

import { Page } from "../api.types";
import type {
    IShareableRecipient,
    ISharedConversationBatchRequest,
    ISharedConversationBatchResponse,
    ISharedConversationCreateRequest,
    ISharedConversationCreateResponse,
    ISharedConversationListItem,
    ITeamShareResponse,
    ShareConversationErrorCode,
} from "./shared-conversations.types";
import { ShareConversationError } from "./shared-conversations.types";

const API = `${API_URL}/chat`;

/**
 * Single source of truth for which `detail.code` values get translated into
 * a typed `ShareConversationError`. The `satisfies Record<union, true>`
 * constraint fails the build if a code is added to
 * `ShareConversationErrorCode` and forgotten here, or vice versa.
 */
const TYPED_SHARE_ERROR_CODES = {
    already_shared: true,
    cross_org: true,
    self_share: true,
    share_disabled: true,
    domain_not_allowed: true,
} as const satisfies Record<ShareConversationErrorCode, true>;

/**
 * Every typed share-rejection code as a runtime array, derived from the
 * `satisfies Record<ShareConversationErrorCode, true>` map above so adding a
 * union member without a key here is a compile error. The error-code contract
 * test iterates this, which is what makes "a new backend code can't silently
 * degrade to the generic message" enforceable in CI rather than aspirational.
 */
export const SHARE_ERROR_CODES = Object.keys(
    TYPED_SHARE_ERROR_CODES
) as ShareConversationErrorCode[];

/**
 * The generic, code-agnostic fallback copy (network / 5xx / unknown). A *typed*
 * code resolving to THIS string is exactly the regression the contract test
 * guards: every `ShareConversationErrorCode` must map to its own specific line.
 */
export const GENERIC_SHARE_ERROR_MESSAGE = "Something went wrong.";

/**
 * Inline copy for a typed share rejection. Single source of truth for both the
 * single-share and batch-share surfaces (the popover renders one of these per
 * failed recipient, keyed by `code`). `recipient` is the human label — a
 * display name for an org member, or the typed/returned email otherwise; batch
 * failures only carry an email, so callers pass that.
 *
 * The exhaustive `switch` (with the `never` guard) is the compile-time half of
 * the contract the error-code test enforces at runtime: a new
 * `ShareConversationErrorCode` fails to build until it gets its own line here,
 * so no code can silently degrade to `GENERIC_SHARE_ERROR_MESSAGE`.
 */
export function errorMessageFor(
    code: ShareConversationErrorCode,
    recipient: string
): string {
    switch (code) {
        case "already_shared":
            return `You've already shared this with ${recipient}.`;
        case "cross_org":
            return `${recipient} belongs to another organization.`;
        case "self_share":
            return "You can't share with yourself.";
        case "share_disabled":
            return "Sharing isn't available for your organization. Contact your administrator if you believe this is incorrect.";
        case "domain_not_allowed":
            return "This recipient's domain isn't permitted by your organization's sharing policy. Contact your administrator to request a change.";
        default: {
            const _exhaustive: never = code;
            return _exhaustive;
        }
    }
}

/**
 * POST /chat/conversations/{id}/share. Backend returns 201 with
 * `ISharedConversationCreateResponse`; 400 + 409 rejections arrive with
 * `{ detail: { code: ShareConversationErrorCode } }` which we translate
 * into a typed `ShareConversationError` so downstream code can
 * pattern-match exhaustively. Any other failure (network, 500, malformed
 * payload) re-throws the raw axios error unchanged.
 */
export const shareConversation = async (
    conversationId: number | string,
    body: ISharedConversationCreateRequest
) => {
    try {
        const response = await axios.post<ISharedConversationCreateResponse>(
            `${API}/conversations/${conversationId}/share`,
            body
        );
        return response.data;
    } catch (err) {
        const typed = toTypedShareError(err);
        if (typed) {
            throw typed;
        }
        throw err;
    }
};

/**
 * POST /chat/conversations/{id}/shares — share with N recipients in one call.
 * Returns 200 with `ISharedConversationBatchResponse`: per-recipient results in
 * deduped request order plus rollup counts. Crucially, PARTIAL success is still
 * a 200 (`failed > 0`), not an HTTP error — each recipient's outcome lives in
 * `results[i].status`/`code`, so there is no typed-error translation here. Only
 * a transport-level failure (network / 5xx / malformed body) rejects, and it
 * rejects with the raw axios error so the popover can show its generic toast.
 */
export const shareConversationBatch = async (
    conversationId: number | string,
    body: ISharedConversationBatchRequest
) => {
    const response = await axios.post<ISharedConversationBatchResponse>(
        `${API}/conversations/${conversationId}/shares`,
        body
    );
    return response.data;
};

/**
 * POST /chat/conversations/{id}/team-share — make a conversation visible to the
 * whole org. Kill-switch-gated (403 `share_disabled` when sharing is off) and
 * rate-limited (429) on the shared Polly-share bucket; both reject with the raw
 * axios error so the toggle can roll back its optimistic state and show the
 * right message. Idempotent — re-enabling an already-team-visible conversation
 * returns 200 with `newly_enabled: false`.
 */
export const enableTeamShare = async (
    conversationId: number | string
): Promise<ITeamShareResponse> => {
    const response = await axios.post<ITeamShareResponse>(
        `${API}/conversations/${conversationId}/team-share`
    );
    return response.data;
};

/**
 * DELETE /chat/conversations/{id}/team-share — revoke org-wide visibility.
 * Deliberately NOT kill-switch-gated (a user can always revoke their own
 * share); shares the same rate-limit bucket as enable. Returns 200 with
 * `newly_enabled: false`.
 */
export const disableTeamShare = async (
    conversationId: number | string
): Promise<ITeamShareResponse> => {
    const response = await axios.delete<ITeamShareResponse>(
        `${API}/conversations/${conversationId}/team-share`
    );
    return response.data;
};

/**
 * GET /chat/shared-conversations: one page of the "Shared with me" list.
 */
export const getSharedConversations = async (
    page = 1,
    size = 50,
    { signal }: { signal?: AbortSignal } = {}
) => {
    const response = await axios.get<Page<ISharedConversationListItem>>(
        buildUrl(`${API}/shared-conversations`, { page, size }),
        { signal }
    );
    return response.data;
};

/**
 * DELETE /chat/shared-conversations/{share_id}: collaborator self-removes
 * their grant.
 *
 * 404 is treated as success (idempotent self-remove). The grant either
 * never existed or was removed by a previous in-flight call; either way
 * the post-condition the caller wants ("this share is gone for me") is
 * satisfied. Anything else propagates as an axios error so optimistic
 * removal in `assistant-ui` can roll back on real failures.
 */
export const removeSharedConversation = async (
    shareId: number | string
): Promise<void> => {
    try {
        await axios.delete(`${API}/shared-conversations/${shareId}`);
    } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 404) {
            return;
        }
        throw err;
    }
};

/**
 * GET /chat/shareable-recipients?q=<query>&size=20. Typeahead roster for
 * the share popover. Empty query returns the org-wide roster sorted by
 * display_name (no "type-to-search" empty state). Top-level under /chat/
 * to avoid the route collision called out in S4.I with
 * /chat/conversations/{id}/*.
 *
 * Accepts an `AbortSignal` so React Query's queryFn can cancel stale
 * keystroke requests. Without it, five characters typed inside the
 * 250 ms debounce window land five concurrent in-flight requests on a
 * slow connection; the last response wins by accident, not by intent.
 */
export const getShareableRecipients = async (
    query: string = "",
    size: number = 20,
    signal?: AbortSignal
) => {
    const url = buildUrl(`${API}/shareable-recipients`, {
        q: query,
        size,
    });
    const response = await axios.get<Page<IShareableRecipient>>(url, {
        signal,
    });
    return response.data;
};

/**
 * Safely extract a typed `ShareConversationError` from an unknown thrown
 * value. Each property access is guarded with a runtime check; no
 * `as AxiosError<ShapedResponse>` cast is trusted to validate the wire
 * payload, so a backend schema drift surfaces as a cache miss here (and
 * the raw axios error re-throws) rather than a silent swallow.
 */
function toTypedShareError(err: unknown): ShareConversationError | null {
    if (!axios.isAxiosError(err)) {
        return null;
    }
    const data: unknown = err.response?.data;
    if (!data || typeof data !== "object") {
        return null;
    }
    const detail = (data as { detail?: unknown }).detail;
    if (!detail || typeof detail !== "object") {
        return null;
    }
    const code = (detail as { code?: unknown }).code;
    if (typeof code !== "string" || !(code in TYPED_SHARE_ERROR_CODES)) {
        return null;
    }
    return new ShareConversationError(code as ShareConversationErrorCode);
}
